import type { VisibilityFlags } from "../../engines/types";
import { useT } from "../../i18n";
import "./visibility-bar.css";
import {
	matchesPreset,
	VISIBILITY_PRESETS,
	type VisibilityPresetId,
} from "./visibilityPresets";

const PRESET_ORDER: VisibilityPresetId[] = ["finished", "framing", "safety"];
const PRESET_LABEL = {
	finished: "vis_presetFinished",
	framing: "vis_presetFraming",
	safety: "vis_presetSafety",
} as const satisfies Record<VisibilityPresetId, string>;

interface VisibilityBarProps {
	visibility: VisibilityFlags;
	onToggle: (key: keyof VisibilityFlags) => void;
	/** Apply a whole-view preset in one update. Absent: no presets shown. */
	onApply?: (visibility: VisibilityFlags) => void;
}

/* ── SVG layer icons (16×16 viewBox) ── */

function IconExterior() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Simple house outline */}
			<path d="M3 8.5V13.5H13V8.5" />
			<path d="M1.5 9L8 3L14.5 9" />
		</svg>
	);
}

function IconRoof() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Triangle roof shape */}
			<path d="M2 12L8 4L14 12" />
			<line x1="2" y1="12" x2="14" y2="12" />
		</svg>
	);
}

function IconInterior() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Bench / interior — two horizontal planks */}
			<path d="M3 6H13" />
			<path d="M4 10H12" />
			<path d="M3 6V10" />
			<path d="M13 6V10" />
			<path d="M4 10V13" />
			<path d="M12 10V13" />
		</svg>
	);
}

function IconFraming() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Structural studs */}
			<rect x="2" y="3" width="12" height="10" />
			<line x1="6" y1="3" x2="6" y2="13" />
			<line x1="10" y1="3" x2="10" y2="13" />
			<line x1="2" y1="8" x2="14" y2="8" />
		</svg>
	);
}

function IconPanel() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Layered cladding panels */}
			<rect x="3" y="3" width="10" height="10" rx="0.5" />
			<line x1="3" y1="6" x2="13" y2="6" />
			<line x1="3" y1="9" x2="13" y2="9" />
			<line x1="3" y1="12" x2="13" y2="12" />
		</svg>
	);
}

function IconFrontWall() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Front-facing wall with door */}
			<rect x="2" y="4" width="12" height="9" />
			<rect x="6" y="7" width="4" height="6" />
		</svg>
	);
}

function IconDeck() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Platform / deck boards */}
			<path d="M1 8L4 6H12L15 8" />
			<line x1="4" y1="6" x2="4" y2="12" />
			<line x1="12" y1="6" x2="12" y2="12" />
			<line x1="4" y1="7.5" x2="12" y2="7.5" />
			<line x1="4" y1="9.5" x2="12" y2="9.5" />
			<line x1="4" y1="11.5" x2="12" y2="11.5" />
		</svg>
	);
}

function IconFoundation() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Ground / piers base */}
			<line x1="2" y1="10" x2="14" y2="10" />
			<rect x="4" y="6" width="2" height="4" />
			<rect x="10" y="6" width="2" height="4" />
			<line x1="1" y1="13" x2="15" y2="13" />
			<line x1="4" y1="10" x2="4" y2="13" />
			<line x1="12" y1="10" x2="12" y2="13" />
		</svg>
	);
}

function IconHeaterClearance() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Heater body with dashed clearance surround */}
			<rect x="6" y="6" width="4" height="7" />
			<path d="M7 6V4.5M9 6V4.5" />
			<path d="M2 2H5" strokeDasharray="2 1.6" />
			<path d="M11 2H14" strokeDasharray="2 1.6" />
			<path d="M2 2V13.5M14 2V13.5" strokeDasharray="2 1.6" />
		</svg>
	);
}

function IconVentilation() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Round plate valve with airflow arrows in and out */}
			<circle cx="8" cy="8" r="3" />
			<path d="M1.5 4.5H4.5" />
			<path d="M3.2 3.2L4.5 4.5L3.2 5.8" />
			<path d="M14.5 11.5H11.5" />
			<path d="M12.8 10.2L11.5 11.5L12.8 12.8" />
		</svg>
	);
}

const ICON_MAP: Record<keyof VisibilityFlags, React.ComponentType> = {
	exterior: IconExterior,
	roof: IconRoof,
	interior: IconInterior,
	framing: IconFraming,
	panel: IconPanel,
	frontWall: IconFrontWall,
	deck: IconDeck,
	foundation: IconFoundation,
	heaterClearance: IconHeaterClearance,
	ventilation: IconVentilation,
};

interface LayerDef {
	key: keyof VisibilityFlags;
	label: string;
}

interface LayerGroup {
	id: string;
	label: string;
	layers: LayerDef[];
}

/** Arrow-key roving inside one `role="toolbar"` group.
 *
 * The rail is a vertical stack on desktop and a horizontal strip on small
 * screens, so both axes move focus. Every button stays in the tab order (no
 * roving tabindex) — this is additive to the default Tab behaviour, never a
 * replacement for it. */
function handleGroupKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
	const { key } = event;
	if (
		key !== "ArrowDown" &&
		key !== "ArrowUp" &&
		key !== "ArrowRight" &&
		key !== "ArrowLeft" &&
		key !== "Home" &&
		key !== "End"
	) {
		return;
	}

	const buttons = Array.from(
		event.currentTarget.querySelectorAll<HTMLButtonElement>(
			"button.visibility-bar-btn",
		),
	);
	const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
	if (current === -1 || buttons.length === 0) return;

	event.preventDefault();
	let next: number;
	if (key === "Home") {
		next = 0;
	} else if (key === "End") {
		next = buttons.length - 1;
	} else {
		const step = key === "ArrowDown" || key === "ArrowRight" ? 1 : -1;
		next = (current + step + buttons.length) % buttons.length;
	}
	buttons[next]?.focus();
}

export function VisibilityBar({
	visibility,
	onToggle,
	onApply,
}: VisibilityBarProps) {
	const t = useT();

	// Presentation-only grouping. This clusters toggles by what the user is
	// reasoning about (the sauna's outer shell, its structural layers, and
	// analysis overlays) — it does NOT change the engine's visibility-group
	// ownership, which stays keyed on VisibilityFlags. See docs/notes/
	// 2026-03-11-visibility-toggles-need-complete-group-ownership.md.
	const GROUPS: LayerGroup[] = [
		{
			id: "shell",
			label: t.vis_groupShell,
			layers: [
				{ key: "exterior", label: t.vis_exterior },
				{ key: "roof", label: t.vis_roof },
				{ key: "interior", label: t.vis_interior },
			],
		},
		{
			id: "structure",
			label: t.vis_groupStructure,
			layers: [
				{ key: "framing", label: t.vis_framing },
				{ key: "panel", label: t.vis_panel },
				{ key: "frontWall", label: t.vis_frontWall },
				{ key: "deck", label: t.vis_deck },
				{ key: "foundation", label: t.vis_foundation },
			],
		},
		{
			id: "analysis",
			label: t.vis_groupAnalysis,
			layers: [
				{ key: "heaterClearance", label: t.vis_heaterClearance },
				{ key: "ventilation", label: t.vis_ventilation },
			],
		},
	];

	return (
		<details name="viewer-tools" className="visibility-disclosure">
			<summary>{t.vis_layers}</summary>
		{onApply && (
			<div
				className="visibility-presets"
				role="group"
				aria-label={t.vis_presetsLabel}
			>
				{PRESET_ORDER.map((id) => {
					const preset = VISIBILITY_PRESETS[id];
					const active = matchesPreset(visibility, preset);
					return (
						<button
							key={id}
							type="button"
							className={`visibility-preset${active ? " active" : ""}`}
							aria-pressed={active}
							onClick={() => onApply(preset)}
						>
							{t[PRESET_LABEL[id]]}
						</button>
					);
				})}
			</div>
		)}
		<div className="visibility-bar">
			{GROUPS.map((group) => (
				<div
					key={group.id}
					className={`visibility-group visibility-group--${group.id}`}
					role="toolbar"
					aria-label={group.label}
					onKeyDown={handleGroupKeyDown}
				>
					<span className="visibility-group-label" aria-hidden="true">
						{group.label}
					</span>
					<div className="visibility-group-toggles">
						{group.layers.map(({ key, label }) => {
							const Icon = ICON_MAP[key];
							const isVisible = visibility[key];
							// Lead with the verb so the button reads as the action it
							// *will* perform on click — earlier wording ("Interior"
							// alone) made users think they were toggling ON a layer
							// that was already on, then watch it disappear.
							const actionVerb = isVisible
								? t.vis_actionHide
								: t.vis_actionShow;
							const actionLabel = `${actionVerb} ${label.toLowerCase()}`;
							return (
								<button
									key={key}
									type="button"
									className={`visibility-bar-btn${isVisible ? " active" : ""}`}
									onClick={() => onToggle(key)}
									title={actionLabel}
									aria-label={actionLabel}
									aria-pressed={isVisible}
								>
									<span className="visibility-bar-icon" aria-hidden="true">
										<Icon />
									</span>
									<span className="visibility-bar-label">{label}</span>
								</button>
							);
						})}
					</div>
				</div>
			))}
		</div>
		</details>
	);
}
